import type { TournamentWSClient, OHLCVData, TimeSeriesResponse } from '../sdk'
import { resolveMappedFullSymbol, resolveMappedShortSymbol, translateIntervalForAPI, transformBarsFromAPI } from './utils'
import { generateFakeOHLCBars, calculatePriceRange } from './demo-data'
import { OperationStore } from '../store'
import { chartDebugLog } from '../debug/browser-logging'

export interface ChartCandleData {
  id?: string
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
  impVolatility?: number
  openInterest?: number
  typicalPrice?: number
  sequence?: number
}

export interface AggregationPeriod {
  type?: string
  duration?: number
  durationType?: string
  value?: number
}

export interface FullChartDataOptions {
  fromTime?: number
  toTime?: number
  fullData?: boolean
  extendedHours?: boolean
  priceType?: string
  candleAlignment?: string
  priceIncrement?: number
}

export interface ChartDataOptions {
  extendedHours?: boolean
  priceType?: string
}

export interface ServiceData {
  prevDayClosePrice?: number
  prePostMarketClose?: number
  bid?: number
  ask?: number
}

export interface CandleDataProvider {
  requestHistoryData(
    symbol: string,
    aggregation: AggregationPeriod,
    options?: FullChartDataOptions,
    updateCallback?: (data: ChartCandleData[]) => void,
    chartId?: string
  ): Promise<ChartCandleData[]>

  subscribeCandles(
    symbol: string,
    aggregation: AggregationPeriod,
    subscriptionId: string,
    subscribeCallback: (data: ChartCandleData[]) => void,
    options?: ChartDataOptions,
    chartId?: string
  ): void

  unsubscribeCandles(subscriptionId: string): void
}

export interface ServiceDataProvider {
  subscribeServiceData(symbol: string, subscribeCallback: (data: ServiceData) => void): void
  unsubscribeServiceData(symbol: string): void
}

export interface ChartDataProvider extends CandleDataProvider, ServiceDataProvider {
  disconnect?(): void
  reconnect?(): void
  setHistoryDataListener?(listener?: (event: HistoryDataLoadedEvent) => void): void
  setSyncDiagnosticsListener?(listener?: (event: SyncDiagnostics) => void): void
  setReplayBarrier?(replayTimeIso: string): void
  clearReplayBarrier?(): void
  syncAfterFastForward?(replayAnchorTimeIso: string, jumpToken?: number): Promise<void>
  refreshChart(): void
}

interface CandleSubscriber {
  subscriptionId: string
  symbol: string
  aggregation: AggregationPeriod
  callback: (data: ChartCandleData[]) => void
  lastCandle?: ChartCandleData
  lastPrice?: number
}

interface ServiceDataSubscriber {
  symbol: string
  callback: (data: ServiceData) => void
}

export type HistoryRequestSource = 'fullData' | 'lazy' | 'subscription' | 'fallback'

export interface HistoryDataLoadedEvent {
  fullSymbol: string
  shortSymbol: string
  source: HistoryRequestSource
  interval: string
  bars: number
  chartId?: string
}

/**
 * Phase 3: Sync lifecycle diagnostics for observability
 * Tracks start, supersede, success, and failure events during fast-forward synchronization
 */
export type SyncLifecycleEvent = 'start' | 'supersede' | 'success' | 'timeout' | 'failure'

export interface SyncDiagnostics {
  event: SyncLifecycleEvent
  token: number
  timestamp: number
  replayAnchor?: string
  subscriptionCount?: number
  failedRefetches?: number
  totalRefetches?: number
  error?: string
}

interface HistoryRequestWindow {
  startDate: string
  endDate: string
  outputsize: number
}

interface InflightHistoryRequestEntry {
  promise: Promise<ChartCandleData[]>
  callbacks: Set<(data: ChartCandleData[]) => void>
  source: HistoryRequestSource
  symbol: string
  interval: string
  request: HistoryRequestWindow
  startedAt: number
}

interface PreloadedHistoryEntry {
  shortSymbol: string
  fullSymbol: string
  interval: string
  startMs: number
  endMs: number
  candles: ChartCandleData[]
}

function subscriberAggregationKey(symbol: string, aggregation: AggregationPeriod): string {
  return `${symbol}|${JSON.stringify(aggregation)}`
}

export class TournamentChartDataProvider implements ChartDataProvider {
  private candleSubscribers: Map<string, CandleSubscriber> = new Map()
  private serviceDataSubscribers: Map<string, ServiceDataSubscriber> = new Map()
  private priceUnsubscriber?: () => void
  private latestPrices: Record<string, number> = {}
  private inflightRequests: Map<string, InflightHistoryRequestEntry> = new Map()
  private chartApi?: any
  private historyDataListener?: (event: HistoryDataLoadedEvent) => void
  private syncDiagnosticsListener?: (event: SyncDiagnostics) => void
  private preloadedHistory?: PreloadedHistoryEntry

  /**
   * Replay barrier timestamp (milliseconds) set during fast-forward synchronization.
   * Incoming PRICE_UPDATED ticks older than this barrier are ignored to prevent
   * stale pre-jump ticks from contaminating post-jump candles.
   * Cleared after synchronization completes.
   */
  private replayBarrierMs?: number

  /**
   * Monotonic jump token to ensure only the latest fast-forward sync takes effect.
   * Incremented on each new fast-forward response; callbacks ignore stale tokens.
   */
  private lastJumpToken: number = 0

  /**
   * Timestamp of the last processed sync operation to prevent network flooding.
   */
  private lastSyncTimestamp: number = 0

  /**
   * In-flight sync operation that can be superseded by a newer jump.
   */
  private inflightSyncPromise?: Promise<void>

  /**
   * Feature flag to enable Phase 2 fallback (unsubscribe/resubscribe) if Phase 1 (refreshChart) is insufficient.
   * Set to true to use the unsubscribe/resubscribe cycle instead of direct callback application.
   */
  private usePhase2Fallback: boolean = false

  private readonly MAX_OUTPUTSIZE = 5000
  private readonly DEFAULT_FALLBACK_OUTPUTSIZE = 1200
  private readonly VIEWPORT_BUFFER_BARS = 32

  constructor(
    private wsClient: TournamentWSClient,
    private symbolShortNames: Record<string, string>,
    private symbolFullNames: Record<string, string>,
    private store: OperationStore,
    private config: {
      useDemoData?: boolean
      startDate?: string
      endDate?: string
      preloadedData?: TimeSeriesResponse
      isHistorical?: boolean
    } = {}
  ) {
    this.preloadedHistory = this.buildPreloadedHistoryEntry(config.preloadedData)
  }

  private resolveToShortName(symbol: string): string {
    return resolveMappedShortSymbol(symbol, this.symbolFullNames, this.symbolShortNames) || symbol
  }

  private resolveToFullName(symbol: string): string {
    return resolveMappedFullSymbol(symbol, this.symbolFullNames, this.symbolShortNames) || symbol
  }

  /**
   * Returns the current effective 'now' timestamp in milliseconds.
   * In historical matches, this should follow the replay clock's currentReplayTime.
   * Fallback to the tournament endDate or system wall-clock if no replay context exists.
   */
  private getEffectiveNowMs(): number {
    const replayTimeIso = this.store.getState().currentReplayTime
    if (replayTimeIso) {
      const ts = Date.parse(replayTimeIso)
      if (!Number.isNaN(ts)) {
        return ts
      }
    }

    // Fallback for historical mode: use the tournament boundaries
    if (this.config.isHistorical) {
      const cutoffMs = this.resolveCutoffTimestampMs()
      if (cutoffMs !== undefined) {
        return cutoffMs
      }

      if (this.config.startDate) {
        const startMs = Date.parse(this.config.startDate)
        if (!Number.isNaN(startMs)) {
          return startMs
        }
      }
    }

    return Date.now()
  }

  async requestHistoryData(
    symbolFullName: string,
    aggregation: AggregationPeriod,
    options?: FullChartDataOptions,
    updateCallback?: (data: ChartCandleData[]) => void,
    chartId?: string
  ): Promise<ChartCandleData[]> {
    try {
      const resolvedSymbolFullName = this.resolveToFullName(symbolFullName)
      const shortName = this.resolveToShortName(symbolFullName)
      chartDebugLog('data', 'requestHistoryData - resolved symbol', {
        fullName: symbolFullName,
        resolvedFullName: resolvedSymbolFullName,
        shortName,
      })
      const interval = translateIntervalForAPI(aggregation)
      const requestSource = this.resolveHistoryRequestSource(options, updateCallback)

      if (!interval) {
        console.warn('Unknown interval:', aggregation)
        return []
      }

      const request = this.ensureChronologicalRange(this.buildHistoryRequest(interval, options, requestSource))
      const normalizedRequest = this.normalizeRequestForDedupe(request, interval, options)
      chartDebugLog('data', 'history request', {
        source: requestSource,
        symbol: shortName,
        interval,
        start: normalizedRequest.startDate,
        end: normalizedRequest.endDate,
        outputsize: normalizedRequest.outputsize,
        callbackProvided: !!updateCallback,
      })

      const preloadedCandles = this.tryGetPreloadedHistory(
        shortName,
        resolvedSymbolFullName,
        interval,
        requestSource,
        normalizedRequest,
        chartId,
      )
      if (preloadedCandles) {
        if (updateCallback) {
          try {
            updateCallback(preloadedCandles)
          } catch (callbackError) {
            console.error('[dx-data] Error invoking preloaded history callback:', callbackError)
          }
          this.refreshChart()
        }

        if (this.historyDataListener) {
          try {
            this.historyDataListener({
              fullSymbol: symbolFullName,
              shortSymbol: shortName,
              source: requestSource,
              interval,
              bars: preloadedCandles.length,
              chartId,
            })
          } catch (listenerError) {
            console.error('[dx-data] Error in history data listener:', listenerError)
          }
        }

        return preloadedCandles
      }

      // Phase 1B: Request-level deduplication
      const cacheKey = this.buildHistoryCacheKey(shortName, interval, requestSource, normalizedRequest)
      const existingEntry = this.inflightRequests.get(cacheKey)
      if (existingEntry) {
        if (updateCallback) {
          existingEntry.callbacks.add(updateCallback)
        }
        chartDebugLog('data', 'deduplicated history request', {
          source: requestSource,
          callbacksRegistered: existingEntry.callbacks.size,
        })
        return existingEntry.promise
      }

      const callbacks = new Set<(data: ChartCandleData[]) => void>()
      if (updateCallback) {
        callbacks.add(updateCallback)
      }

      const requestStartedAt = Date.now()
      const inflightEntry: InflightHistoryRequestEntry = {
        promise: Promise.resolve([]),
        callbacks,
        source: requestSource,
        symbol: shortName,
        interval,
        request: normalizedRequest,
        startedAt: requestStartedAt,
      }

      const timeSeriesPromise = (async () => {
        const timeSeriesResponse = await this.wsClient.timeSeries({
          symbol: shortName,
          interval,
          start_date: normalizedRequest.startDate,
          end_date: normalizedRequest.endDate,
          order: 'desc',
          outputsize: normalizedRequest.outputsize,
        })

        let bars = this.normalizeBars(timeSeriesResponse.data)

        if (this.config.useDemoData) {
          const priceRange = calculatePriceRange(bars)
          const fakeOHLC = generateFakeOHLCBars(bars, priceRange)
          bars = fakeOHLC
        }

        const transformedBars = transformBarsFromAPI(bars)
        const chartCandles = this.ohlcvToChartCandles(transformedBars)
        const elapsedMs = Date.now() - inflightEntry.startedAt
        const callbacksRegistered = inflightEntry.callbacks.size
        chartDebugLog('data', 'history response', {
          source: requestSource,
          symbol: shortName,
          interval,
          bars: chartCandles.length,
          elapsedMs,
          callbacksRegistered,
        })

        if (callbacksRegistered > 0) {
          let invokedCallbacks = 0
          for (const callback of Array.from(inflightEntry.callbacks)) {
            try {
              callback(chartCandles)
              invokedCallbacks += 1
            } catch (callbackError) {
              console.error('[dx-data] Error invoking history callback:', callbackError)
            }
          }
          chartDebugLog('data', 'invoked history callbacks', {
            source: requestSource,
            symbol: shortName,
            count: invokedCallbacks,
          })
          // Refresh the chart after data is loaded to trigger re-render
          this.refreshChart()
        }

        if (this.historyDataListener) {
          try {
            this.historyDataListener({
              fullSymbol: symbolFullName,
              shortSymbol: shortName,
              source: requestSource,
              interval,
              bars: chartCandles.length,
              chartId,
            })
          } catch (listenerError) {
            console.error('[dx-data] Error in history data listener:', listenerError)
          }
        }

        return chartCandles
      })()

      // Store the pending request and clean up when done
      inflightEntry.promise = timeSeriesPromise
      this.inflightRequests.set(cacheKey, inflightEntry)
      timeSeriesPromise.finally(() => {
        this.inflightRequests.delete(cacheKey)
      })

      return timeSeriesPromise
    } catch (error) {
      const shortName = this.resolveToShortName(symbolFullName)
      const interval = translateIntervalForAPI(aggregation)
      const isTimeout = error instanceof Error && (error.message.includes('timeout') || error.message.includes('Timeout'))
      console.error(
        `[dx-data] history load FAILED symbol=${shortName} interval=${interval} isTimeout=${isTimeout}`,
        error
      )
      return []
    }
  }

  private buildPreloadedHistoryEntry(preloadedData?: TimeSeriesResponse): PreloadedHistoryEntry | undefined {
    if (!preloadedData || !Array.isArray(preloadedData.data) || preloadedData.data.length === 0) {
      return undefined
    }

    const shortSymbol = preloadedData.symbol
    const fullSymbol = this.symbolFullNames[shortSymbol] || shortSymbol
    const bars = this.normalizeBars(preloadedData.data)
    if (bars.length === 0) {
      return undefined
    }

    const transformedBars = transformBarsFromAPI(bars)
    const candles = this.ohlcvToChartCandles(transformedBars)
    if (candles.length === 0) {
      return undefined
    }

    const startMs = candles[0].time
    const endMs = candles[candles.length - 1].time
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      return undefined
    }

    return {
      shortSymbol,
      fullSymbol,
      interval: preloadedData.interval,
      startMs,
      endMs,
      candles,
    }
  }

  private tryGetPreloadedHistory(
    shortName: string,
    symbolFullName: string,
    interval: string,
    requestSource: HistoryRequestSource,
    request: HistoryRequestWindow,
    chartId?: string,
  ): ChartCandleData[] | undefined {
    const preloaded = this.preloadedHistory
    if (!preloaded) {
      return undefined
    }

    if (preloaded.shortSymbol !== shortName || preloaded.interval !== interval) {
      return undefined
    }

    const requestStartMs = Date.parse(request.startDate)
    const requestEndMs = Date.parse(request.endDate)
    if (Number.isNaN(requestStartMs) || Number.isNaN(requestEndMs)) {
      return undefined
    }

    if (requestStartMs < preloaded.startMs || requestEndMs > preloaded.endMs) {
      return undefined
    }

    // Task 2.3: Bypass preloaded cache if the request asks for significantly more data than we have preloaded.
    // This ensures that 'fullData' or large viewport requests aren't prematurely satisfied by a small preloaded chunk,
    // which would then be cleared, preventing further high-fidelity fetches.
    const preloadedCount = preloaded.candles.length
    if (request.outputsize > preloadedCount * 1.5 && request.outputsize > 1000) {
      chartDebugLog('data', 'bypassing preloaded history: request exceeds preloaded count', {
        requestOutputsize: request.outputsize,
        preloadedCount,
      })
      return undefined
    }

    const candles = preloaded.candles.filter((candle) => candle.time >= requestStartMs && candle.time <= requestEndMs)
    if (candles.length === 0) {
      return undefined
    }

    // One-shot optimization to avoid stale preloaded data reuse.
    this.preloadedHistory = undefined

    chartDebugLog('data', 'preloaded history hit', {
      source: requestSource,
      symbol: shortName,
      interval,
      bars: candles.length,
      chartId: chartId ?? 'n/a',
    })

    return candles
  }

  private buildHistoryRequest(
    interval: string,
    options?: FullChartDataOptions,
    requestSource: HistoryRequestSource = 'fallback'
  ): HistoryRequestWindow {
    const intervalMs = this.intervalToMilliseconds(interval)
    if (!intervalMs) {
      throw new Error(`unsupported interval: ${interval}`)
    }

    const cutoffMs = this.resolveCutoffTimestampMs()
    const nowMs = this.getEffectiveNowMs()
    const boundedNowMs = cutoffMs !== undefined ? Math.min(nowMs, cutoffMs) : nowMs
    let fromTimeMs = this.normalizeChartTimestamp(options?.fromTime)
    let toTimeMs = this.normalizeChartTimestamp(options?.toTime)

    // Task 2.1: Automatically shift requests that target the future wall-clock time into the match era.
    // If the request is for the current wall-clock era but we are in a historical match (nowMs is in the past),
    // shift the request window to end at our effective 'now'.
    if (this.config.isHistorical) {
      const wallClockNowMs = Date.now()
      const isWallClockRequest = (toTimeMs !== undefined && toTimeMs > wallClockNowMs - 1000 * 60 * 10) ||
                                 (fromTimeMs !== undefined && fromTimeMs > wallClockNowMs - 1000 * 60 * 60 * 24)

      if (isWallClockRequest && nowMs < wallClockNowMs - 1000 * 60 * 60 * 24) {
        let shiftDelta = wallClockNowMs - nowMs

        // Task 5.1: Snap shiftDelta to the current interval to stabilize cache keys.
        if (intervalMs) {
          shiftDelta = Math.floor(shiftDelta / intervalMs) * intervalMs
        }

        chartDebugLog('data', 'shifting wall-clock history request into match era', {
          deltaMs: shiftDelta,
          nowMs,
          wallClockNowMs,
        })
        if (toTimeMs !== undefined) toTimeMs -= shiftDelta
        if (fromTimeMs !== undefined) fromTimeMs -= shiftDelta
      }
    }

    // Initial/viewport load: fromTime is available, return at least that range.
    if (fromTimeMs !== undefined) {
      const requestedEndMs = toTimeMs !== undefined ? toTimeMs : boundedNowMs
      const endMs = cutoffMs !== undefined ? Math.min(requestedEndMs, cutoffMs) : requestedEndMs
      const maxStartMs = endMs - intervalMs * this.MAX_OUTPUTSIZE
      const requestedStartMs = Math.min(fromTimeMs, endMs)
      const startMs = Math.max(requestedStartMs, maxStartMs)
      const rawBars = Math.ceil((endMs - startMs) / intervalMs) + 1 + this.VIEWPORT_BUFFER_BARS
      const outputsize = Math.min(this.MAX_OUTPUTSIZE, Math.max(1, rawBars))

      return {
        startDate: new Date(startMs).toISOString(),
        endDate: new Date(endMs).toISOString(),
        outputsize,
      }
    }

    // Lazy left load: chart requests older candles with only toTime.
    if (toTimeMs !== undefined) {
      const endMs = cutoffMs !== undefined ? Math.min(toTimeMs, cutoffMs) : toTimeMs
      const startMs = endMs - intervalMs * this.MAX_OUTPUTSIZE
      return {
        startDate: new Date(startMs).toISOString(),
        endDate: new Date(endMs).toISOString(),
        outputsize: this.MAX_OUTPUTSIZE,
      }
    }

    // Fallback: bounded "now" chunk for first render or unknown chart options.
    const fallbackOutputsize =
      requestSource === 'fullData' ? this.MAX_OUTPUTSIZE : this.DEFAULT_FALLBACK_OUTPUTSIZE
    const fallbackEndMs = boundedNowMs
    const fallbackStartMs = fallbackEndMs - intervalMs * fallbackOutputsize
    return {
      startDate: new Date(fallbackStartMs).toISOString(),
      endDate: new Date(fallbackEndMs).toISOString(),
      outputsize: fallbackOutputsize,
    }
  }

  private resolveHistoryRequestSource(
    options?: FullChartDataOptions,
    updateCallback?: (data: ChartCandleData[]) => void
  ): HistoryRequestSource {
    if (options?.fullData) {
      return 'fullData'
    }

    const hasFromTime = this.normalizeChartTimestamp(options?.fromTime) !== undefined
    const hasToTime = this.normalizeChartTimestamp(options?.toTime) !== undefined

    if (hasToTime && !hasFromTime) {
      return 'lazy'
    }

    if (hasFromTime || updateCallback) {
      return 'subscription'
    }

    return 'fallback'
  }

  private normalizeRequestForDedupe(
    request: HistoryRequestWindow,
    interval: string,
    options?: FullChartDataOptions
  ): HistoryRequestWindow {
    const hasFromTime = this.normalizeChartTimestamp(options?.fromTime) !== undefined
    const hasToTime = this.normalizeChartTimestamp(options?.toTime) !== undefined
    if (hasFromTime || hasToTime) {
      return request
    }

    const intervalMs = this.intervalToMilliseconds(interval)
    if (!intervalMs) {
      return request
    }

    const startMs = Date.parse(request.startDate)
    const endMs = Date.parse(request.endDate)
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
      return request
    }

    // Task 6.1: Boundary Bug Mitigation
    // Historical history requests that land exactly on interval boundaries often trigger 
    // truncation or "future-only" data from the backend. We subtract 1ms from the endDate
    // before snapping for historical matches, ensuring results always include the last closed candle.
    let endMsForSnapping = endMs
    if (this.config.isHistorical) {
      endMsForSnapping -= 1
    }

    const snappedEndMs = Math.floor(endMsForSnapping / intervalMs) * intervalMs
    const rawSnappedStartMs = snappedEndMs - (endMs - startMs)
    const snappedStartMs = Math.floor(rawSnappedStartMs / intervalMs) * intervalMs

    return this.ensureChronologicalRange({
      ...request,
      startDate: new Date(snappedStartMs).toISOString(),
      endDate: new Date(snappedEndMs).toISOString(),
    })
  }

  private buildHistoryCacheKey(
    symbol: string,
    interval: string,
    source: HistoryRequestSource,
    request: HistoryRequestWindow
  ): string {
    return [symbol, interval, source, request.startDate, request.endDate, String(request.outputsize)].join('::')
  }

  private ensureChronologicalRange(request: {
    startDate: string
    endDate: string
    outputsize: number
  }): {
    startDate: string
    endDate: string
    outputsize: number
  } {
    const startMs = Date.parse(request.startDate)
    const endMs = Date.parse(request.endDate)
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || startMs <= endMs) {
      return request
    }

    console.warn(
      `[dx-data] Invalid history range start > end; normalizing start=${request.startDate} end=${request.endDate}`
    )
    return {
      ...request,
      startDate: new Date(endMs).toISOString(),
      endDate: new Date(startMs).toISOString(),
    }
  }

  private normalizeBars(bars: OHLCVData[]): OHLCVData[] {
    const deduped = new Map<number, OHLCVData>()

    for (const bar of bars) {
      const ts = Date.parse(bar.timestamp)
      if (Number.isNaN(ts)) {
        continue
      }
      deduped.set(ts, bar)
    }

    return Array.from(deduped.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([, bar]) => bar)
  }

  private normalizeChartTimestamp(raw?: number): number | undefined {
    if (raw === undefined || raw === null || !Number.isFinite(raw)) {
      return undefined
    }

    // Support both seconds and milliseconds chart timestamps.
    if (Math.abs(raw) < 1_000_000_000_000) {
      return raw * 1000
    }

    return raw
  }

  private resolveCutoffTimestampMs(): number | undefined {
    if (!this.config.endDate) {
      return undefined
    }

    const cutoffMs = Date.parse(this.config.endDate)
    if (Number.isNaN(cutoffMs)) {
      console.warn('[dx-data] Invalid cutoff endDate provided:', this.config.endDate)
      return undefined
    }

    return cutoffMs
  }

  private intervalToMilliseconds(interval: string): number | undefined {
    switch (interval) {
      case '1m':
      case '1min':
        return 60 * 1000
      case '5m':
      case '5min':
        return 5 * 60 * 1000
      case '15m':
      case '15min':
        return 15 * 60 * 1000
      case '30m':
      case '30min':
        return 30 * 60 * 1000
      case '1h':
        return 60 * 60 * 1000
      case '2h':
        return 2 * 60 * 60 * 1000
      case '4h':
        return 4 * 60 * 60 * 1000
      case '1d':
      case '1day':
        return 24 * 60 * 60 * 1000
      case '1w':
      case '1week':
        return 7 * 24 * 60 * 60 * 1000
      case '1mo':
      case '1month':
        return 30 * 24 * 60 * 60 * 1000
      default:
        return undefined
    }
  }

  /**
   * Task 1.2: Derives the history-replacement end timestamp from the rendered candle grid.
   *
   * The raw replay anchor is a continuous point in display time, but the chart renders candles
   * on an interval-specific grid. This helper aligns the history refetch end to the visible
   * last candle by computing the next expected grid timestamp after the raw anchor, then
   * selecting the last grid-aligned timestamp that does not exceed the raw anchor.
   *
   * Returns the grid-aligned timestamp (ms), or the raw anchor if alignment cannot be computed.
   */
  private alignToChartGrid(
    replayAnchorMs: number,
    subscriber: CandleSubscriber
  ): number {
    const { lastCandle, aggregation } = subscriber
    if (!lastCandle) {
      // No prior candle data: cannot align; use raw anchor
      chartDebugLog('fastForward', 'grid alignment skipped: no lastCandle available', {})
      return replayAnchorMs
    }

    const interval = translateIntervalForAPI(aggregation)
    if (!interval) {
      chartDebugLog('fastForward', 'grid alignment skipped: unknown interval', { aggregation })
      return replayAnchorMs
    }

    const intervalMs = this.intervalToMilliseconds(interval)
    if (!intervalMs) {
      chartDebugLog('fastForward', 'grid alignment skipped: cannot convert interval to ms', { interval })
      return replayAnchorMs
    }

    const lastCandleMs = lastCandle.time
    if (!Number.isFinite(lastCandleMs)) {
      console.warn('[dx-data] Grid alignment invalid: lastCandle.time is not finite', lastCandle.time)
      return replayAnchorMs
    }

    // Compute the grid offset from the last known candle
    const timeSinceLastCandle = replayAnchorMs - lastCandleMs
    const candlesAfterLast = Math.floor(timeSinceLastCandle / intervalMs)

    // The aligned history end is the last grid-aligned timestamp that does not exceed the raw anchor
    const alignedMs = lastCandleMs + (candlesAfterLast * intervalMs)

    chartDebugLog('fastForward', 'grid alignment computed', {
      lastCandle: new Date(lastCandleMs).toISOString(),
      replayAnchor: new Date(replayAnchorMs).toISOString(),
      intervalMs,
      candlesAfterLast,
      aligned: new Date(alignedMs).toISOString(),
      diffMinutes: ((replayAnchorMs - alignedMs) / 1000 / 60).toFixed(2),
    })

    return alignedMs
  }

  subscribeCandles(
    symbolFullName: string,
    aggregation: AggregationPeriod,
    subscriptionId: string,
    subscribeCallback: (data: ChartCandleData[]) => void,
    options?: ChartDataOptions,
    chartId?: string
  ): void {
    const resolvedSymbolFullName = this.resolveToFullName(symbolFullName)
    chartDebugLog('data', 'subscribeCandles', {
      symbol: symbolFullName,
      resolvedSymbol: resolvedSymbolFullName,
      subscriptionId,
    })
    const subscriber: CandleSubscriber = {
      subscriptionId,
      symbol: resolvedSymbolFullName,
      aggregation,
      callback: subscribeCallback,
    }

    this.candleSubscribers.set(subscriptionId, subscriber)

    if (this.candleSubscribers.size === 1 && this.serviceDataSubscribers.size === 0) {
      this.attachPriceUpdates()
    }

    // Request history data to populate the subscriber's initial state
    // DXChart's real-time updates depend on having initial candles to build on
    this.requestHistoryData(resolvedSymbolFullName, aggregation, options, subscribeCallback, chartId)
      .then((historyBars) => {
        if (!historyBars || historyBars.length === 0) {
          return
        }

        const activeSubscriber = this.candleSubscribers.get(subscriptionId)
        if (!activeSubscriber) {
          return
        }

        const lastHistoryBar = historyBars[historyBars.length - 1]
        activeSubscriber.lastCandle = lastHistoryBar
        activeSubscriber.lastPrice = lastHistoryBar.close
      })
      .catch(err => {
        console.error('[dx-data] Failed to load history for subscribeCandles:', resolvedSymbolFullName, err)
      })
  }

  unsubscribeCandles(subscriptionId: string): void {
    this.candleSubscribers.delete(subscriptionId)

    if (this.candleSubscribers.size === 0 && this.serviceDataSubscribers.size === 0) {
      this.detachPriceUpdates()
    }
  }

  subscribeServiceData(symbolFullName: string, subscribeCallback: (data: ServiceData) => void): void {
    const resolvedSymbolFullName = this.resolveToFullName(symbolFullName)
    const shortName = this.resolveToShortName(symbolFullName)
    chartDebugLog('data', 'subscribeServiceData - resolved symbol', {
      fullName: symbolFullName,
      resolvedFullName: resolvedSymbolFullName,
      shortName,
    })

    this.serviceDataSubscribers.set(shortName, {
      symbol: resolvedSymbolFullName,
      callback: subscribeCallback,
    })

    if (this.candleSubscribers.size === 0 && this.serviceDataSubscribers.size === 1) {
      this.attachPriceUpdates()
    }

    const currentPrice = this.latestPrices[shortName]
    if (currentPrice !== undefined) {
      subscribeCallback({
        bid: currentPrice,
        ask: currentPrice,
      })
    }
  }

  unsubscribeServiceData(symbolFullName: string): void {
    const shortName = this.resolveToShortName(symbolFullName)
    chartDebugLog('data', 'unsubscribeServiceData - resolved symbol', { fullName: symbolFullName, shortName })
    this.serviceDataSubscribers.delete(shortName)

    if (this.candleSubscribers.size === 0 && this.serviceDataSubscribers.size === 0) {
      this.detachPriceUpdates()
    }
  }

  disconnect(): void {
    this.detachPriceUpdates()
    this.candleSubscribers.clear()
    this.serviceDataSubscribers.clear()
  }

  reconnect(): void {
    // no-op; connection is managed externally
  }

  setChartApi(api: any): void {
    this.chartApi = api
  }

  setHistoryDataListener(listener?: (event: HistoryDataLoadedEvent) => void): void {
    this.historyDataListener = listener
  }

  /**
   * Phase 3: Register a listener for sync lifecycle diagnostics
   */
  setSyncDiagnosticsListener(listener?: (event: SyncDiagnostics) => void): void {
    this.syncDiagnosticsListener = listener
  }

  /**
   * Phase 3: Emit a sync diagnostics event
   */
  private emitSyncDiagnostics(event: SyncDiagnostics): void {
    if (this.syncDiagnosticsListener) {
      try {
        this.syncDiagnosticsListener(event)
      } catch (err) {
        console.error('[dx-data] Error in syncDiagnosticsListener:', err)
      }
    }
  }

  refreshChart(): void {
    if (this.chartApi?.refresh) {
      try {
        chartDebugLog('data', 'calling chart.refresh() after data load', {})
        this.chartApi.refresh()
      } catch (err) {
        console.error('[dx-data] Error calling chart.refresh():', err)
      }
    }
  }

  private attachPriceUpdates(): void {
    if (this.priceUnsubscriber) {
      return // already attached
    }

    this.priceUnsubscriber = this.wsClient.on('PRICE_UPDATED', (msg: any) => {
      const { symbol, price, updatedAt } = msg.payload
      const shortName = this.resolveToShortName(symbol)
      const numPrice = Number(price)

      this.latestPrices[shortName] = numPrice

      const symbolFullName = this.resolveToFullName(shortName)
      if (!symbolFullName) {
        return
      }

      // Parse updatedAt timestamp (authoritative server time for bucket selection).
      // Backward compatible: falls back to getEffectiveNowMs() if updatedAt is not provided.
      // This allows older servers to coexist with newer chart-widget implementations.
      const serverTimestampMs = updatedAt ? new Date(updatedAt).getTime() : this.getEffectiveNowMs()

      // Replay barrier check (Task 4.3): Ignore stale pre-jump ticks during fast-forward synchronization
      // Boundary rule: ticks at or after barrier are valid; ticks before barrier are dropped
      if (this.replayBarrierMs !== undefined && serverTimestampMs < this.replayBarrierMs) {
        chartDebugLog('fastForward', 'ignoring pre-barrier tick', {
          symbol: shortName,
          timestampMs: serverTimestampMs,
          barrierMs: this.replayBarrierMs,
        })
        return
      }

      // Update candle subscribers
      Array.from(this.candleSubscribers.values()).forEach((subscriber) => {
        if (subscriber.symbol === symbolFullName) {
          const oldLastCandle = subscriber.lastCandle
          const newCandle = this.createOrUpdateCandle(subscriber.lastCandle, numPrice, subscriber.aggregation, serverTimestampMs)
          subscriber.lastCandle = newCandle
          subscriber.lastPrice = numPrice

          // DIAGNOSTIC: Log PRICE_UPDATED candle creation
          if (oldLastCandle && newCandle.time !== oldLastCandle.time) {
            // New candle was created (time bucket changed)
            const candleJump = (newCandle.time - oldLastCandle.time) / 1000 / 60
            chartDebugLog('data', 'PRICE_UPDATED: new candle', {
              candleJump,
              oldTime: new Date(oldLastCandle.time).toISOString(),
              newTime: new Date(newCandle.time).toISOString(),
            })
          } else if (oldLastCandle) {
            // Existing candle updated (no debug log for high-frequency updates)
          }

          subscriber.callback([newCandle])
        }
      })

      // Update service data subscribers
      Array.from(this.serviceDataSubscribers.values()).forEach((subscriber) => {
        if (subscriber.symbol === symbolFullName) {
          subscriber.callback({
            bid: numPrice,
            ask: numPrice,
          })
        }
      })
    })
  }

  private detachPriceUpdates(): void {
    if (this.priceUnsubscriber) {
      this.priceUnsubscriber()
      this.priceUnsubscriber = undefined
    }
  }

  private ohlcvToChartCandles(bars: any[]): ChartCandleData[] {
    return bars.map((bar) => ({
      time: Math.floor(bar.time / 1000) * 1000,
      open: Number(bar.open),
      high: Number(bar.high),
      low: Number(bar.low),
      close: Number(bar.close),
      volume: Number(bar.volume || 0),
    }))
  }

  private createOrUpdateCandle(
    lastCandle: ChartCandleData | undefined,
    price: number,
    aggregation: AggregationPeriod,
    serverTimestampMs?: number,
  ): ChartCandleData {
    const interval = translateIntervalForAPI(aggregation)
    const intervalMs = interval ? this.intervalToMilliseconds(interval) : undefined
    // Use server-provided timestamp (from updatedAt) for bucket selection.
    // Fall back to getEffectiveNowMs() if not provided.
    const timestampMs = serverTimestampMs ?? this.getEffectiveNowMs()
    const currentBucketTime = intervalMs
      ? Math.floor(timestampMs / intervalMs) * intervalMs
      : Math.floor(timestampMs / 1000) * 1000

    if (!lastCandle) {
      return {
        time: currentBucketTime,
        open: price,
        high: price,
        low: price,
        close: price,
        volume: 0,
      }
    }

    if (currentBucketTime > lastCandle.time) {
      chartDebugLog('data', 'createOrUpdateCandle: NEW CANDLE', {
        old: new Date(lastCandle.time).toISOString(),
        new: new Date(currentBucketTime).toISOString(),
        open: lastCandle.close,
      })
      return {
        time: currentBucketTime,
        open: lastCandle.close,
        high: Math.max(lastCandle.close, price),
        low: Math.min(lastCandle.close, price),
        close: price,
        volume: 0,
      }
    }

    // Same bucket, update existing
    return {
      ...lastCandle,
      high: Math.max(lastCandle.high, price),
      low: Math.min(lastCandle.low, price),
      close: price,
    }
  }

  /**
   * Sets the replay barrier for fast-forward synchronization (Task 4.3).
   * During jump synchronization, incoming PRICE_UPDATED ticks older than this
   * barrier will be ignored to prevent stale pre-jump ticks from contaminating
   * post-jump candles.
   *
   * @param currentReplayTimeIso - Current replay time as ISO timestamp from fast-forward response
   */
  setReplayBarrier(currentReplayTimeIso: string): void {
    try {
      const barrierMs = new Date(currentReplayTimeIso).getTime()
      if (!Number.isFinite(barrierMs)) {
        throw new Error('Invalid replay barrier timestamp')
      }
      this.replayBarrierMs = barrierMs
      chartDebugLog('fastForward', 'Replay barrier set', { barrierTime: currentReplayTimeIso, barrierMs: this.replayBarrierMs })
    } catch (err) {
      console.error('[dx-data] Failed to parse replay barrier timestamp:', currentReplayTimeIso, err)
    }
  }

  /**
   * Clears the replay barrier after fast-forward synchronization completes.
   */
  clearReplayBarrier(): void {
    this.replayBarrierMs = undefined
    chartDebugLog('fastForward', 'Replay barrier cleared')
  }

  /**
   * Synchronizes candles after a fast-forward jump (Task 1.2, 1.3).
   * Fetches and applies updated candles for all active subscriptions anchored to the new replay time.
   *
   * The synchronization is guarded by a monotonic jump token: if a newer fast-forward
   * arrives before this sync completes, the newer sync supersedes this one.
   *
   * @param replayAnchorTimeIso - ISO timestamp of the jump replay time
   * @param jumpToken - Optional monotonic token; if omitted, auto-increments lastJumpToken
   */
  async syncAfterFastForward(replayAnchorTimeIso: string, jumpToken?: number): Promise<void> {
    // Task 5.2: Sync Throttling - prevent network flooding during rapid replaying (e.g. slider dragging)
    const now = Date.now()
    if (now - this.lastSyncTimestamp < 200) {
      return
    }
    this.lastSyncTimestamp = now

    // Auto-increment jump token if not provided
    const token = jumpToken ?? ++this.lastJumpToken

    // DIAGNOSTIC: Log entry with current state
    const activeSubscribers = Array.from(this.candleSubscribers.values())
    chartDebugLog('fastForward', 'syncAfterFastForward STARTED', {
      token,
      replayAnchor: replayAnchorTimeIso,
      activeSubscribers: activeSubscribers.length,
    })

    // Phase 3: Emit 'start' diagnostic
    this.emitSyncDiagnostics({
      event: 'start',
      token,
      timestamp: Date.now(),
      replayAnchor: replayAnchorTimeIso,
    })

    // If a newer sync is already in flight, it supersedes this older one
    if (token < this.lastJumpToken) {
      chartDebugLog('fastForward', 'syncAfterFastForward superseded by newer token', {
        newerToken: this.lastJumpToken,
        ignoredToken: token,
      })

      // Phase 3: Emit 'supersede' diagnostic
      this.emitSyncDiagnostics({
        event: 'supersede',
        token,
        timestamp: Date.now(),
        replayAnchor: replayAnchorTimeIso,
      })
      return
    }

    try {
      // Parse anchor time
      let anchorMs: number
      try {
        anchorMs = new Date(replayAnchorTimeIso).getTime()
        if (!Number.isFinite(anchorMs)) {
          throw new Error('Invalid timestamp')
        }
      } catch (err) {
        console.error('[dx-data] Failed to parse replay anchor timestamp:', replayAnchorTimeIso, err)
        throw err
      }

      // Task 2.2: Collect unique (symbol, interval) pairs from active candle subscribers
      // Deduplication ensures each subscription is refetched once, and the alignment uses
      // the advanced lastCandle from the previous jump for subsequent jumps.
      const subscriptionMap = new Map<string, CandleSubscriber>()
      for (const subscriber of this.candleSubscribers.values()) {
        const key = subscriberAggregationKey(subscriber.symbol, subscriber.aggregation)
        if (!subscriptionMap.has(key)) {
          subscriptionMap.set(key, subscriber)
        }
      }

      chartDebugLog('fastForward', 'syncAfterFastForward found subscriptions', { count: subscriptionMap.size })

      // Refetch and apply candles for each active subscription
      const refetchPromises: Promise<void>[] = []
      for (const subscriber of subscriptionMap.values()) {
        // Task 5.5: Concurrent Jump Guard - check if a newer jump happened while we were iterating
        if (token < this.lastJumpToken) {
          chartDebugLog('fastForward', 'sync loop aborted: newer jump detected')
          break
        }
        const promise = this.refetchAndApplyCandles(subscriber, anchorMs, token)
        refetchPromises.push(promise)
      }

      // Wait for all refetches to settle (fail-safe: don't throw if individual refetches fail)
      const results = await Promise.allSettled(refetchPromises)
      const failures = results.filter((r) => r.status === 'rejected')

      if (failures.length > 0) {
        console.warn('[dx-data] Some candle refetches failed during syncAfterFastForward:', failures.length, '/', results.length)

        // Phase 3: Emit 'failure' diagnostic with failure count
        this.emitSyncDiagnostics({
          event: 'failure',
          token,
          timestamp: Date.now(),
          replayAnchor: replayAnchorTimeIso,
          subscriptionCount: subscriptionMap.size,
          failedRefetches: failures.length,
          totalRefetches: results.length,
        })
      } else {
        chartDebugLog('fastForward', 'All candle refetches completed successfully', { totalRefetches: results.length })

        // Phase 3: Emit 'success' diagnostic
        this.emitSyncDiagnostics({
          event: 'success',
          token,
          timestamp: Date.now(),
          replayAnchor: replayAnchorTimeIso,
          subscriptionCount: subscriptionMap.size,
          totalRefetches: results.length,
        })
      }
    } catch (err) {
      // Log but don't re-throw; sync failure is non-blocking
      console.error('[dx-data] syncAfterFastForward error:', err)

      // Phase 3: Emit 'failure' diagnostic with error
      this.emitSyncDiagnostics({
        event: 'failure',
        token,
        timestamp: Date.now(),
        replayAnchor: replayAnchorTimeIso,
        error: err instanceof Error ? err.message : String(err),
      })
    }
  }

  /**
   * Refetches and applies candles for a single subscriber (Task 1.3).
   *
   * Design Decision 3: The replay barrier (raw anchor) and history refetch end (aligned timestamp)
   * serve distinct purposes:
   * - Replay barrier uses the raw anchor to filter stale pre-jump ticks precisely.
   * - History refetch uses the aligned timestamp to render the expected candle frontier.
   *
   * This separation preserves replay-safe semantics while aligning the visible chart to the
   * rendered candle grid.
   *
   * If the jump token is stale, this is a no-op.
   */
  private async refetchAndApplyCandles(
    subscriber: CandleSubscriber,
    anchorMs: number,
    jumpToken: number
  ): Promise<void> {
    const { symbol, aggregation, subscriptionId } = subscriber

    // Sanity check token before starting refetch
    if (jumpToken < this.lastJumpToken) {
      chartDebugLog('fastForward', 'refetchAndApplyCandles skipped (stale token)', {
        staleToken: jumpToken,
        currentToken: this.lastJumpToken,
      })
      return
    }

    chartDebugLog('fastForward', 'refetchAndApplyCandles started', { symbol, subscriptionId })

    try {
      // Task 2.1: Align history refetch end to rendered candle grid instead of using raw anchor
      const alignedHistoryEnd = this.alignToChartGrid(anchorMs, subscriber)

      chartDebugLog('fastForward', 'History refetch aligned', {
        rawAnchor: anchorMs,
        alignedEnd: alignedHistoryEnd,
        difference: anchorMs - alignedHistoryEnd,
      })

      // Task 5.3: Reduced Sync Window: Use explicit 1000 candle window for sync refetches
      // to reduce network load while still providing enough context for the viewport.
      const interval = translateIntervalForAPI(aggregation)
      const intervalMs = (interval ? this.intervalToMilliseconds(interval) : undefined) || 60000
      const startMs = alignedHistoryEnd - (intervalMs * 1000)

      // Task 5.5: Concurrent Jump Guard - final check before initiating network request
      if (jumpToken < this.lastJumpToken) {
        chartDebugLog('fastForward', 'refetchAndApplyCandles aborted before network (stale token)', {
          jumpToken,
          lastJumpToken: this.lastJumpToken,
        })
        return
      }

      const candles = await this.requestHistoryData(symbol, aggregation, {
        fromTime: startMs,
        toTime: alignedHistoryEnd,
      })

      // Diagnostic logging for OHLC fidelity and gap detection
      if (candles.length > 0) {
        const flatCount = candles.filter(c => c.high === c.low).length
        const firstCandle = candles[0]
        const lastCandle = candles[candles.length - 1]

        const timeRangeMinutes = (lastCandle.time - firstCandle.time) / 1000 / 60
        const expectedMinutes = Math.round((alignedHistoryEnd - startMs) / 1000 / 60)
        chartDebugLog('fastForward', 'sync result', {
          symbol,
          total: candles.length,
          flat: flatCount,
          timeRangeMinutes,
          expectedMinutes,
          firstTime: new Date(firstCandle.time).toISOString(),
          lastTime: new Date(lastCandle.time).toISOString(),
        })

        // Detect gaps in candle sequence
        const gaps: Array<{at: number, gap: number}> = []
        for (let i = 1; i < candles.length; i++) {
          const timeDiff = candles[i].time - candles[i-1].time
          if (Math.abs(timeDiff - intervalMs) > 100) { // Allow 100ms tolerance
            gaps.push({at: i, gap: timeDiff - intervalMs})
          }
        }
        if (gaps.length > 0) {
          console.warn(`[dx-data] GAPS DETECTED in refetch: ${gaps.length} gaps found`)
          gaps.slice(0, 5).forEach(g => console.warn(`[dx-data]   Gap at candle[${g.at}]: +${g.gap}ms`))
        } else {
          chartDebugLog('fastForward', '✓ no gaps detected', { symbol, count: candles.length })
        }
      }

      // Re-check token before applying (stale sync must not override newer jump)
      if (jumpToken < this.lastJumpToken) {
        chartDebugLog('fastForward', 'refetchAndApplyCandles result ignored (stale token)', {
          jumpToken,
          lastJumpToken: this.lastJumpToken,
        })
        return
      }

      // Apply refreshed candles via subscriber callback
      if (candles.length > 0) {
        chartDebugLog('fastForward', 'applying candles', { symbol, count: candles.length })

        const lastHistoryBar = candles[candles.length - 1]
        const subscriberKey = subscriberAggregationKey(symbol, aggregation)

        if (this.usePhase2Fallback) {
          // Phase 2 fallback: unsubscribe/resubscribe cycle
          // This forces DXChart through its normal initialization flow, which handles bulk history correctly.
          chartDebugLog('fastForward', 'using Phase 2 fallback (unsubscribe/resubscribe)', { symbol })

          for (const activeSubscriber of this.candleSubscribers.values()) {
            if (subscriberAggregationKey(activeSubscriber.symbol, activeSubscriber.aggregation) !== subscriberKey) {
              continue
            }

            const { subscriptionId, symbol: symName, aggregation: agg, callback } = activeSubscriber

            // Unsubscribe from the current feed
            chartDebugLog('fastForward', 'Phase 2: unsubscribing', { symName, subscriptionId })
            this.unsubscribeCandles(subscriptionId)

            // Resubscribe with the same parameters to trigger normal initialization
            chartDebugLog('fastForward', 'Phase 2: resubscribing', { symName, subscriptionId })
            this.subscribeCandles(symName, agg, subscriptionId, callback, undefined, undefined)

            // Update the resubscribed subscriber with the latest candle info
            const resubscribedSubscriber = this.candleSubscribers.get(subscriptionId)
            if (resubscribedSubscriber) {
              resubscribedSubscriber.lastCandle = lastHistoryBar
              resubscribedSubscriber.lastPrice = lastHistoryBar.close
            }
          }
        } else {
          // Phase 1: direct callback application with repaint
          // DXChart subscription callbacks are designed for incremental real-time updates, not bulk history replacement.
          // If this is insufficient and candles render incorrectly (flat OHLC), switch usePhase2Fallback to true.
          for (const activeSubscriber of this.candleSubscribers.values()) {
            if (subscriberAggregationKey(activeSubscriber.symbol, activeSubscriber.aggregation) !== subscriberKey) {
              continue
            }

            // DIAGNOSTIC: Log state before callback
            const oldLastCandle = activeSubscriber.lastCandle
            chartDebugLog('fastForward', 'Phase 1: before callback', {
              symbol,
              oldLastCandleTime: oldLastCandle ? new Date(oldLastCandle.time).toISOString() : 'none',
              candles: candles.length,
              firstTime: new Date(candles[0].time).toISOString(),
              lastTime: new Date(candles[candles.length - 1].time).toISOString(),
            })

            activeSubscriber.lastCandle = lastHistoryBar
            activeSubscriber.lastPrice = lastHistoryBar.close

            // DIAGNOSTIC: Log state after update but before callback
            const callbackStartTime = Date.now()

            activeSubscriber.callback(candles)

            const callbackDuration = Date.now() - callbackStartTime
            chartDebugLog('fastForward', 'Phase 1: callback completed', {
              symbol,
              duration: callbackDuration,
            })
          }

          this.refreshChart()
        }
      }
    } catch (err) {
      console.error('[dx-data] refetchAndApplyCandles failed for', symbol, ':', err)
      throw err
    }
  }
}
